import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Plus, Play } from "lucide-react";
import { cn } from "@/cn";
import {
  createSequence,
  listSequences,
  processDueSequences,
  updateSequence,
} from "@/api/sequences";
import { useDebouncedValue } from "@/hooks/usePagination";

export function SequencesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);

  const { data, isLoading } = useQuery({
    queryKey: ["sequences", debouncedQ],
    queryFn: () => listSequences({ q: debouncedQ || undefined }),
    staleTime: 15_000,
  });

  const sequences = (data?.sequences ?? []).filter((s) => !s.archived);

  const createMutation = useMutation({
    mutationFn: () =>
      createSequence({
        name: name.trim() || "Untitled sequence",
        active: false,
        steps: [
          {
            id: crypto.randomUUID(),
            position: 0,
            type: "auto_email",
            waitTime: 0,
            waitMode: "day",
            enabled: true,
            touches: [
              {
                id: crypto.randomUUID(),
                threadMode: "new_thread",
                subject: "Quick question",
                bodyHtml: "<p>Hi {{first_name}},</p>",
                status: "approved",
              },
            ],
          },
          {
            id: crypto.randomUUID(),
            position: 1,
            type: "call",
            waitTime: 2,
            waitMode: "day",
            enabled: true,
            note: "Follow up call",
            touches: [],
          },
          {
            id: crypto.randomUUID(),
            position: 2,
            type: "manual_email",
            waitTime: 3,
            waitMode: "day",
            enabled: true,
            touches: [
              {
                id: crypto.randomUUID(),
                threadMode: "reply_to_thread",
                subject: "Following up",
                bodyHtml: "<p>Just bumping this to the top of your inbox.</p>",
                status: "approved",
              },
            ],
          },
        ],
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["sequences"] });
      setCreateOpen(false);
      setName("");
      navigate(`/sequences/${res.sequence.sequenceId}`);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Create failed");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      updateSequence(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sequences"] }),
  });

  const processMutation = useMutation({
    mutationFn: processDueSequences,
    onSuccess: (r) => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["sequences"] });
      alert(
        `Processed ${r.processed}: ${r.tasksCreated} tasks, ${r.finished} finished`,
      );
    },
  });

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-brand" />
            <h1 className="text-2xl font-bold text-foreground">Sequences</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Multi-step outreach cadences for contacts — emails, calls, and
            tasks.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search sequences…"
            className="w-48 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={() => processMutation.mutate()}
            disabled={processMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50"
          >
            <Play className="h-3.5 w-3.5" />
            Process due
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            Create sequence
          </button>
        </div>
      </header>

      {error ? (
        <p className="mb-4 text-sm text-red-600">{error}</p>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading sequences…</p>
      ) : sequences.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">
            No sequences yet. Create one from scratch to enroll contacts.
          </p>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="mt-4 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground"
          >
            Create sequence
          </button>
        </div>
      ) : (
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-border bg-muted/30 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Steps</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2">Finished</th>
                <th className="px-3 py-2">Replied</th>
                <th className="px-3 py-2">Bounced</th>
              </tr>
            </thead>
            <tbody>
              {sequences.map((s) => (
                <tr
                  key={s.sequenceId}
                  className="border-b border-border/70 last:border-0 hover:bg-muted/10"
                >
                  <td className="px-3 py-2.5">
                    <Link
                      to={`/sequences/${s.sequenceId}`}
                      className="font-medium text-foreground hover:text-brand"
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() =>
                        toggleMutation.mutate({
                          id: s.sequenceId,
                          active: !s.active,
                        })
                      }
                      className={cn(
                        "rounded-full px-2 py-0.5 text-xs font-medium",
                        s.active
                          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                          : "bg-muted/40 text-muted-foreground",
                      )}
                    >
                      {s.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">
                    {s.stats.numSteps}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">
                    {s.stats.activeEnrollments}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">
                    {s.stats.finishedEnrollments}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">
                    {s.stats.uniqueReplied}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">
                    {s.stats.uniqueBounced}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-lg">
            <h2 className="text-lg font-semibold">Create sequence</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Starts with a 3-step template (email → call → follow-up). Edit
              steps after create.
            </p>
            <label className="mt-4 block text-sm">
              Name
              <input
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Outbound — ICP A"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="rounded-md border border-border px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={createMutation.isPending}
                onClick={() => createMutation.mutate()}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
